import './styles/PaginatedSlider.scss';

import { X } from '@phosphor-icons/react';
import React, {
  Children,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useRef,
} from 'react';

import { useClassHelper, useOutsideClick } from '../../hooks';
import { Paragraph } from '../Typography';
import { Controls } from './components/Controls';
import { Page } from './components/Page';
import { TimelineNavigation } from './components/TimelineNavigation';
import { componentName } from './constants';
import { usePaginatedSlider } from './hooks/usePaginatedSlider';

const PRELUDE = 'prelude';

type PageElement = ReactElement<{ title: string; visible?: boolean }>;

interface Props extends PropsWithChildren {
  header: string;
  onDismiss: () => void;
}

export function PaginatedSlider({ children, header, onDismiss }: Props) {
  const c = useClassHelper(componentName);
  const ref = useRef(null);

  const filteredChildren = Children.toArray(children)
    .filter(child => isValidElement(child) && child.type === Page)
    .filter(child => (child as PageElement).props.visible !== false);

  const {
    goForward,
    goBackward,
    goTo,
    validKeys: pageKeys,
    currentPageKey,
    currentPageIndex,
  } = usePaginatedSlider(filteredChildren, PRELUDE);

  const handleDismiss = () => {
    onDismiss();
  };

  useOutsideClick(ref, handleDismiss);

  const isPrelude = currentPageKey === PRELUDE;

  return (
    <div className={componentName} ref={ref}>
      <header className={c('__header', isPrelude ? '__header--prelude' : '')}>
        <Paragraph isBolder>{header}</Paragraph>

        <button
          type="button"
          onClick={handleDismiss}
          className={c('__header__close-button')}
        >
          <X size={24} aria-label="Close icon" />
        </button>
      </header>
      <section
        className={c('__content', isPrelude ? '__content--prelude' : '')}
      >
        <TimelineNavigation
          currentPageIndex={currentPageIndex}
          totalPages={pageKeys.length}
          preludePage={isPrelude}
          preludePageKey={PRELUDE}
          currentPage={currentPageKey}
          goTo={goTo}
          pages={pageKeys}
        />
        {filteredChildren.map(child => {
          const isInactive =
            (child as PageElement).props.title === currentPageKey;

          return (
            <section
              aria-hidden={!isInactive}
              key={(child as PageElement).props.title}
              className={c('__page_visualizer', isInactive ? '' : '--hide')}
            >
              {child}
            </section>
          );
        })}
      </section>
      <Controls
        preludePage={isPrelude}
        goForward={goForward}
        goBackward={goBackward}
        onDismiss={handleDismiss}
        currentPageIndex={currentPageIndex}
        totalPages={pageKeys.length}
      />
    </div>
  );
}

PaginatedSlider.Page = Page;
PaginatedSlider.const = {
  PRELUDE: PRELUDE,
};
